import { Suspense, lazy, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import ThemeProvider from '@mui/material/styles/ThemeProvider';
import { getTheme } from './theme';
import { AuthProvider } from './hooks/useAuth';
import { ToastProvider } from './components/ToastProvider';
import ProtectedRoute from './components/ProtectedRoute';
import PermissionRoute from './components/PermissionRoute';
import PlatformOwnerRoute from './components/PlatformOwnerRoute';
import AuthLayout from './layouts/AuthLayout';
import MainLayout from './layouts/MainLayout';
import PlatformLayout from './layouts/PlatformLayout';

// Every page is route-split via React.lazy - with ~80 pages eagerly bundled
// the main chunk was 1.6MB. Layouts/guards above stay eager since they're
// needed on first paint regardless of route.
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const WhatsAppSettings = lazy(() => import('./pages/WhatsAppSettings'));
const Contacts = lazy(() => import('./pages/Contacts'));
const Templates = lazy(() => import('./pages/Templates'));
const SharedInbox = lazy(() => import('./pages/SharedInbox'));
const BulkMessaging = lazy(() => import('./pages/BulkMessaging'));
const Broadcast = lazy(() => import('./pages/Broadcast'));
const MessageHistory = lazy(() => import('./pages/MessageHistory'));
const WhatsAppInbox = lazy(() => import('./pages/WhatsAppInbox'));
const CompanyProfile = lazy(() => import('./pages/CompanyProfile'));
const Security = lazy(() => import('./pages/Security'));
const ActivityLogs = lazy(() => import('./pages/ActivityLogs'));
const Subscription = lazy(() => import('./pages/Subscription'));
const Roles = lazy(() => import('./pages/Roles'));
const Departments = lazy(() => import('./pages/Departments'));
const Designations = lazy(() => import('./pages/Designations'));
const Teams = lazy(() => import('./pages/Teams'));
const Support = lazy(() => import('./pages/Support'));
const WebhookLogs = lazy(() => import('./pages/WebhookLogs'));
const Users = lazy(() => import('./pages/Users'));
const ApiKeys = lazy(() => import('./pages/ApiKeys'));
const Branding = lazy(() => import('./pages/Branding'));
const Billing = lazy(() => import('./pages/Billing'));
const Wallet = lazy(() => import('./pages/Wallet'));
const ContactDuplicates = lazy(() => import('./pages/ContactDuplicates'));
const Tags = lazy(() => import('./pages/Tags'));
const Labels = lazy(() => import('./pages/Labels'));
const Groups = lazy(() => import('./pages/Groups'));
const Segments = lazy(() => import('./pages/Segments'));
const CustomFields = lazy(() => import('./pages/CustomFields'));
const Automation = lazy(() => import('./pages/Automation'));
const AutomationBuilder = lazy(() => import('./pages/AutomationBuilder'));
const Chatbots = lazy(() => import('./pages/Chatbots'));
const ChatbotBuilder = lazy(() => import('./pages/ChatbotBuilder'));
const Leads = lazy(() => import('./pages/Leads'));
const Deals = lazy(() => import('./pages/Deals'));
const SalesDashboard = lazy(() => import('./pages/SalesDashboard'));
const Tickets = lazy(() => import('./pages/Tickets'));
const Analytics = lazy(() => import('./pages/Analytics'));
const NotificationSettings = lazy(() => import('./pages/NotificationSettings'));
const WebhookSubscriptions = lazy(() => import('./pages/WebhookSubscriptions'));
const PlatformDashboard = lazy(() => import('./pages/platform/Dashboard'));
const PlatformCompanies = lazy(() => import('./pages/platform/Companies'));
const PlatformUsers = lazy(() => import('./pages/platform/Users'));
const PlatformWalletLedger = lazy(() => import('./pages/platform/WalletLedger'));
const PlatformSubscriptions = lazy(() => import('./pages/platform/Subscriptions'));
const PlatformPlans = lazy(() => import('./pages/platform/Plans'));
const PlatformCoupons = lazy(() => import('./pages/platform/Coupons'));
const PlatformInvoices = lazy(() => import('./pages/platform/Invoices'));
const PlatformPayments = lazy(() => import('./pages/platform/Payments'));
const PlatformRevenue = lazy(() => import('./pages/platform/Revenue'));
const PlatformSupportTickets = lazy(() => import('./pages/platform/SupportTickets'));
const PlatformGlobalSettings = lazy(() => import('./pages/platform/GlobalSettings'));
const PlatformSmtp = lazy(() => import('./pages/platform/Smtp'));
const PlatformStorage = lazy(() => import('./pages/platform/Storage'));
const PlatformFeatureFlags = lazy(() => import('./pages/platform/FeatureFlags'));
const PlatformMaintenanceMode = lazy(() => import('./pages/platform/MaintenanceMode'));
const PlatformRedisMonitor = lazy(() => import('./pages/platform/RedisMonitor'));
const PlatformQueueMonitor = lazy(() => import('./pages/platform/QueueMonitor'));
const PlatformLogs = lazy(() => import('./pages/platform/Logs'));
const PlatformApiMonitor = lazy(() => import('./pages/platform/ApiMonitor'));
const PlatformSystemHealth = lazy(() => import('./pages/platform/SystemHealth'));

function RouteFallback() {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
      <CircularProgress size={28} />
    </Box>
  );
}

export default function App() {
  const themeMode = useSelector((state) => state.ui.themeMode);
  const theme = useMemo(() => getTheme(themeMode), [themeMode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ToastProvider>
        <AuthProvider>
          <BrowserRouter>
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route element={<AuthLayout />}>
                  <Route path="/login" element={<Login />} />
                  <Route path="/register" element={<Register />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/verify-email" element={<VerifyEmail />} />
                </Route>

                <Route element={<ProtectedRoute />}>
                  <Route element={<MainLayout />}>
                    <Route path="/" element={<Dashboard />} />
                    <Route path="/security" element={<Security />} />

                    <Route element={<PermissionRoute module="whatsapp_settings" action="view" />}>
                      <Route path="/settings" element={<WhatsAppSettings />} />
                    </Route>
                    <Route element={<PermissionRoute module="contacts" action="view" />}>
                      <Route path="/contacts" element={<Contacts />} />
                      <Route path="/contacts/duplicates" element={<ContactDuplicates />} />
                    </Route>
                    <Route element={<PermissionRoute module="contacts" action="manage" />}>
                      <Route path="/tags" element={<Tags />} />
                      <Route path="/labels" element={<Labels />} />
                      <Route path="/groups" element={<Groups />} />
                      <Route path="/segments" element={<Segments />} />
                      <Route path="/custom-fields" element={<CustomFields />} />
                    </Route>
                    <Route element={<PermissionRoute module="templates" action="view" />}>
                      <Route path="/templates" element={<Templates />} />
                    </Route>
                    <Route element={<PermissionRoute module="inbox" action="view" />}>
                      <Route path="/inbox" element={<SharedInbox />} />
                    </Route>
                    <Route element={<PermissionRoute module="bulk_send" action="view" />}>
                      <Route path="/bulk-messaging" element={<BulkMessaging />} />
                      <Route path="/campaigns" element={<Broadcast />} />
                    </Route>
                    <Route element={<PermissionRoute module="message_history" action="view" />}>
                      <Route path="/history" element={<MessageHistory />} />
                      <Route path="/whatsapp-inbox" element={<WhatsAppInbox />} />
                    </Route>
                    <Route element={<PermissionRoute module="whatsapp_settings" action="view" />}>
                      <Route path="/webhook-logs" element={<WebhookLogs />} />
                    </Route>
                    <Route element={<PermissionRoute module="company" action="view" />}>
                      <Route path="/company-profile" element={<CompanyProfile />} />
                    </Route>
                    <Route element={<PermissionRoute module="activity_logs" action="view" />}>
                      <Route path="/activity-logs" element={<ActivityLogs />} />
                    </Route>
                    <Route element={<PermissionRoute module="subscription" action="view" />}>
                      <Route path="/subscription" element={<Subscription />} />
                      <Route path="/billing" element={<Billing />} />
                      <Route path="/wallet" element={<Wallet />} />
                    </Route>
                    <Route element={<PermissionRoute module="company" action="manage" />}>
                      <Route path="/branding" element={<Branding />} />
                      <Route path="/api-keys" element={<ApiKeys />} />
                    </Route>
                    <Route element={<PermissionRoute module="team_members" action="view" />}>
                      <Route path="/users" element={<Users />} />
                    </Route>
                    <Route element={<PermissionRoute module="roles" action="view" />}>
                      <Route path="/roles" element={<Roles />} />
                    </Route>
                    <Route element={<PermissionRoute module="departments" action="view" />}>
                      <Route path="/departments" element={<Departments />} />
                    </Route>
                    <Route element={<PermissionRoute module="designations" action="view" />}>
                      <Route path="/designations" element={<Designations />} />
                    </Route>
                    <Route element={<PermissionRoute module="teams" action="view" />}>
                      <Route path="/teams" element={<Teams />} />
                    </Route>
                    <Route path="/support" element={<Support />} />

                    <Route element={<PermissionRoute module="automation" action="view" />}>
                      <Route path="/automation" element={<Automation />} />
                      <Route path="/automation/:id/builder" element={<AutomationBuilder />} />
                    </Route>
                    <Route element={<PermissionRoute module="chatbot" action="view" />}>
                      <Route path="/chatbots" element={<Chatbots />} />
                      <Route path="/chatbots/:id/builder" element={<ChatbotBuilder />} />
                    </Route>
                    <Route element={<PermissionRoute module="crm_leads" action="view" />}>
                      <Route path="/leads" element={<Leads />} />
                    </Route>
                    <Route element={<PermissionRoute module="crm_deals" action="view" />}>
                      <Route path="/deals" element={<Deals />} />
                      <Route path="/sales-dashboard" element={<SalesDashboard />} />
                    </Route>
                    <Route element={<PermissionRoute module="tickets" action="view" />}>
                      <Route path="/tickets" element={<Tickets />} />
                    </Route>
                    <Route element={<PermissionRoute module="analytics" action="view" />}>
                      <Route path="/analytics" element={<Analytics />} />
                    </Route>
                    <Route path="/notification-settings" element={<NotificationSettings />} />
                    <Route element={<PermissionRoute module="webhooks" action="view" />}>
                      <Route path="/webhooks" element={<WebhookSubscriptions />} />
                    </Route>
                  </Route>

                  <Route element={<PlatformOwnerRoute />}>
                    <Route element={<PlatformLayout />}>
                      <Route path="/platform" element={<PlatformDashboard />} />
                      <Route path="/platform/companies" element={<PlatformCompanies />} />
                      <Route path="/platform/users" element={<PlatformUsers />} />
                      <Route path="/platform/wallet-ledger" element={<PlatformWalletLedger />} />
                      <Route path="/platform/subscriptions" element={<PlatformSubscriptions />} />
                      <Route path="/platform/plans" element={<PlatformPlans />} />
                      <Route path="/platform/coupons" element={<PlatformCoupons />} />
                      <Route path="/platform/invoices" element={<PlatformInvoices />} />
                      <Route path="/platform/payments" element={<PlatformPayments />} />
                      <Route path="/platform/revenue" element={<PlatformRevenue />} />
                      <Route path="/platform/support-tickets" element={<PlatformSupportTickets />} />
                      <Route path="/platform/settings" element={<PlatformGlobalSettings />} />
                      <Route path="/platform/settings/smtp" element={<PlatformSmtp />} />
                      <Route path="/platform/settings/storage" element={<PlatformStorage />} />
                      <Route path="/platform/feature-flags" element={<PlatformFeatureFlags />} />
                      <Route path="/platform/settings/maintenance" element={<PlatformMaintenanceMode />} />
                      <Route path="/platform/monitor/redis" element={<PlatformRedisMonitor />} />
                      <Route path="/platform/monitor/queues" element={<PlatformQueueMonitor />} />
                      <Route path="/platform/monitor/logs" element={<PlatformLogs />} />
                      <Route path="/platform/monitor/api" element={<PlatformApiMonitor />} />
                      <Route path="/platform/monitor/health" element={<PlatformSystemHealth />} />
                    </Route>
                  </Route>
                </Route>

                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </AuthProvider>
      </ToastProvider>
    </ThemeProvider>
  );
}
